import React from 'react';
import {
  Button, ButtonColor, ButtonVariant, ICON_ARROW_CIRCLE, Spacing, SpacingName, SymbolDirection, TypographyName,
} from '@doveit/lego';
import { BasicSectionProps } from '../types';
import PageSection from '../../components/page-section/PageSection';
import ArticleMarkdown from '../../components/article-markdown/ArticleMarkdown';
import { CTA } from '../../types';
import CardsHList from '../../components/cards-hlist/CardsHList';
import { Review } from '../../../domain/graphcms';
import ReviewCard from '../../components/review-card/ReviewCard';
import { goToExternalUrl } from '../../utils/url/utils';
import { DOVEIT_TRUSTPILOT_REVIEWS_PAGE } from '../../containers/list-reviews/ListReviews';

export interface ReviewsSectionProps extends BasicSectionProps {
  reviews: Review[],
  trustPilot: CTA,
}

export const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  title,
  content,
  trustPilot,
  reviews,
}) => {
  const goToReviewPage = React.useCallback((url?: string) => () => {
    goToExternalUrl(url ?? DOVEIT_TRUSTPILOT_REVIEWS_PAGE);
  }, []);

  const hasHeader = React.useMemo(() => !!(title || content), [title, content]);

  return (
    <PageSection data-ref="reviews">
      <PageSection.Wrapper>
        {hasHeader && (
          <>
            {title && (
              <PageSection.Title
                size={TypographyName.H3}
                content={title}
              />
            )}
            {content && (
              <PageSection.Block>
                <ArticleMarkdown>
                  {content}
                </ArticleMarkdown>
              </PageSection.Block>
            )}
          </>
        )}
        <Spacing margin={[hasHeader ? SpacingName.SMALLER : 0, 0, 0]}>
          <Button
            href={trustPilot.href}
            label={trustPilot.label}
            rel="nofollow"
            variant={ButtonVariant.LIGHT}
            withoutSpacing
            iconRight
            iconDirection={SymbolDirection.RIGHT}
            icon={ICON_ARROW_CIRCLE}
            color={ButtonColor.PRIMARY}
            dataRef={trustPilot.dataRef}
            dataTracking={trustPilot.dataTracking}
          />
        </Spacing>
      </PageSection.Wrapper>
      <PageSection.Wrapper withoutPad>
        <PageSection.Block>
          <CardsHList
            inline={3}
            sideSpacing={SpacingName.SMALL}
          >
            {reviews.map((review) => (
              <CardsHList.Item
                key={review.id}
              >
                <ReviewCard
                  {...review}
                  onClick={goToReviewPage(review.url)}
                />
              </CardsHList.Item>
            ))}
          </CardsHList>
        </PageSection.Block>
      </PageSection.Wrapper>
    </PageSection>
  );
};

export default ReviewsSection;
